/**
 * AbtretungsErklaerung — A4 Sicherungsabtretung im GDV-/Dokumentations-Stil.
 *
 * Layout-Anker: 1:1 wie /app/frontend/src/components/case/DocumentationStep.jsx
 *   • Helvetica/Arial sans-serif, 10pt body
 *   • Fast-schwarzer Text (#0B1320) auf Weiß, Cyan-Akzent nur in Eyebrows
 *   • A4-Margins 22/20/24 mm, Header (Logo + AZ/SN + Tel/Email),
 *     Footer (Sachverständigen-Daten + Bank + Seitenzahl)
 *   • KV-Tabellen: Label-Spalte mit grauem Background (#F1F5F9),
 *     Value-Spalte weiß, ohne Trennlinien
 *
 * Click-to-Edit: Titel + Text jeder Klausel sind direkt editierbar
 * (gleicher Mechanismus wie vorher — gespeichert in case.abtretung.clauses).
 */
import React, { useMemo, useState, useEffect, useRef } from "react";

// ---------- Design-Konstanten (synchron mit DocumentationStep) ------------
const COLORS = {
  ink: "#0B1320",
  body: "#1f2937",
  mute: "#65718A",
  accent: "var(--brand-1)",
  light: "#F1F5F9",
  border: "#E5E7EB",
  white: "#FFFFFF",
};
const FONT_STACK = "'Helvetica', 'Arial', sans-serif";
const LOGO_URL = "/easy-gutachter-logo.png";

const dash = (v) => (v === null || v === undefined || v === "" ? "—" : v);

const DEFAULT_CLAUSES = [
  {
    id: "c1",
    title: "Abtretung",
    text: "Hiermit trete ich, der oben genannte Geschädigte, meinen Anspruch auf Ersatz der Sachverständigenkosten aus dem Verkehrsunfall vom {{accident_date}} gegen den Fahrer, Halter und dessen Haftpflichtversicherung in Höhe der Gutachterkosten erfüllungshalber an das nachfolgend benannte Sachverständigenbüro ab: {{gutachter_buero}}",
  },
  {
    id: "c2",
    title: "Umfang der Abtretung",
    text: "Die Abtretung umfasst den Anspruch auf Erstattung der Gutachterkosten inklusive aller Nebenforderungen (z. B. Zinsen, Mahnkosten, Rechtsverfolgungskosten), soweit diese im Zusammenhang mit der Gutachtenerstellung stehen.",
  },
  {
    id: "c3",
    title: "Einziehungsbefugnis",
    text: "Das Sachverständigenbüro ist berechtigt, die abgetretenen Ansprüche im eigenen Namen gegenüber der Versicherung bzw. dem Schädiger geltend zu machen und durchzusetzen.",
  },
  {
    id: "c4",
    title: "Sicherungsabtretung / Restforderung",
    text: "Diese Abtretung erfolgt erfüllungshalber. Sollte die Haftpflichtversicherung oder der Schädiger die Sachverständigenkosten nicht oder nicht vollständig begleichen, verpflichte ich mich, den offenen Betrag selbst zu zahlen.",
  },
  {
    id: "c5",
    title: "Hinweis zur Freiwilligkeit",
    text: "Mir ist bekannt, dass ich zur Zahlung des Sachverständigenhonorars verpflichtet bin und diese Abtretung der vereinfachten Schadensabwicklung dient.",
  },
  {
    id: "c6",
    title: "Datenschutz",
    text: "Ich willige ein, dass meine Daten zur Durchsetzung der Forderung an die gegnerische Versicherung sowie ggf. an beauftragte Rechtsanwälte übermittelt werden dürfen.",
  },
];

// Multi-Page-Pagination: max Klauseln pro Seite. Erste Seite hat weniger
// (weil Title + KV-Blöcke Platz brauchen), Folgeseiten mehr.
const FIRST_PAGE_CLAUSE_LIMIT = 3;
const PAGE_CLAUSE_LIMIT = 5;

function getClauses(c) {
  const saved = c?.abtretung?.clauses;
  if (Array.isArray(saved) && saved.length > 0) return saved;
  return DEFAULT_CLAUSES;
}

/**
 * Wie viele A4-Seiten braucht die Abtretungserklärung mit aktuellem Inhalt?
 * Wird vom DocumentationStep aufgerufen, um TOTAL_PAGES korrekt zu zählen.
 */
export function calcAbtretungPageCount(c) {
  const clauses = getClauses(c);
  if (clauses.length <= FIRST_PAGE_CLAUSE_LIMIT) return 1;
  const overflow = clauses.length - FIRST_PAGE_CLAUSE_LIMIT;
  return 1 + Math.ceil(overflow / PAGE_CLAUSE_LIMIT);
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
}

// Underline-Field for `{{placeholder}}` substitution in clause-text
function FieldVal({ value }) {
  const v = (value || "").toString().trim();
  return (
    <span style={{
      display: "inline-block",
      borderBottom: `1pt solid ${COLORS.ink}`,
      padding: "0 4px",
      margin: "0 2px",
      minWidth: "120px",
      color: COLORS.ink,
      fontWeight: 500,
    }}>
      {v || <span style={{ color: COLORS.mute, fontStyle: "italic" }}>—</span>}
    </span>
  );
}

function renderTextWithFields(text, replacements) {
  if (!text) return null;
  const parts = text.split(/(\{\{[a-z_]+\}\})/gi);
  return parts.map((p, i) => {
    const m = p.match(/^\{\{([a-z_]+)\}\}$/i);
    if (m) {
      const key = m[1];
      const r = replacements[key];
      return <FieldVal key={i} value={r?.value || ""} />;
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

// ---------- Layout-Bausteine (1:1 wie DocumentationStep) ------------------
function PageHeader({ doc }) {
  const hasUserLogo = !!doc.letterheadUrl;
  return (
    <div style={{
      position: "absolute", top: "10mm", left: "20mm", right: "20mm",
      display: "flex", justifyContent: "space-between", alignItems: "flex-start",
      paddingBottom: "3mm", borderBottom: `0.5pt solid ${COLORS.border}`,
      fontSize: "8.5pt", color: COLORS.mute,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
        {hasUserLogo ? (
          <img src={doc.letterheadUrl} alt={doc.gutachterCompany || "Logo"}
               style={{ height: "11mm", width: "auto", maxWidth: "60mm", objectFit: "contain" }} />
        ) : (
          <>
            <img src={LOGO_URL} alt="Easy Gutachter" style={{ height: "8mm", width: "auto" }} />
            <span style={{ fontWeight: 700, color: COLORS.ink, letterSpacing: "0.01em", fontSize: "9.5pt" }}>
              Easy Gutachter
            </span>
          </>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", fontSize: "8pt", gap: "1.5mm", textAlign: "right", color: COLORS.mute }}>
        {(doc.caseRef || doc.schadenNr) && (
          <div style={{ display: "flex", gap: "10px" }}>
            {doc.caseRef && <span>AZ: <span style={{ color: COLORS.ink, fontWeight: 600 }}>{doc.caseRef}</span></span>}
            {doc.schadenNr && <span>SN: <span style={{ color: COLORS.ink, fontWeight: 600 }}>{doc.schadenNr}</span></span>}
          </div>
        )}
        {(doc.phone || doc.email) && (
          <div style={{ fontSize: "7.5pt", color: COLORS.mute }}>
            {doc.phone && <span>TEL {doc.phone}</span>}
            {doc.phone && doc.email && <span style={{ margin: "0 6px" }}>·</span>}
            {doc.email && <span>{doc.email}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function PageFooter({ doc }) {
  // Wenn pageNo/totalPages gesetzt sind, integriert sich die Abtretungserklärung
  // nahtlos in die Gesamt-Dokumentation. Ohne diese Props zeigt sie "1 / 1"
  // (Standalone-Modus z.B. im Signature-Step).
  const pageInfo = doc.pageNo && doc.totalPages
    ? `${doc.pageNo} / ${doc.totalPages}`
    : "1 / 1";
  return (
    <div style={{
      position: "absolute", bottom: "12mm", left: "20mm", right: "20mm",
      borderTop: `0.5pt solid ${COLORS.border}`, paddingTop: "3mm",
      fontSize: "7.5pt", color: COLORS.mute,
      display: "flex", justifyContent: "space-between", alignItems: "flex-start",
      lineHeight: 1.5,
    }}>
      <div>
        <div style={{ fontWeight: 700, color: COLORS.ink }}>
          {dash(doc.gutachterName)}{doc.gutachterCompany ? ` · ${doc.gutachterCompany}` : ""}
        </div>
        <div>{dash(doc.gutachterAddress)}</div>
        <div style={{ marginTop: "0.5mm" }}>
          {doc.iban && <span>IBAN {doc.iban}</span>}
          {doc.iban && doc.bic && <span> · </span>}
          {doc.bic && <span>BIC {doc.bic}</span>}
          {(doc.iban || doc.bic) && doc.vatId && <span> · </span>}
          {doc.vatId && <span>USt-ID {doc.vatId}</span>}
        </div>
      </div>
      <div style={{ color: COLORS.ink, fontWeight: 600 }}>{pageInfo}</div>
    </div>
  );
}

function SectionEyebrow({ children }) {
  return (
    <div style={{
      fontSize: "8pt", fontWeight: 700, color: COLORS.accent,
      letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "1.5mm",
    }}>{children}</div>
  );
}

function KVList({ rows }) {
  const visible = rows.filter((r) => r && r[1] !== null && r[1] !== undefined && r[1] !== "");
  if (visible.length === 0) return null;
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10pt", marginTop: "2mm" }}>
      <tbody>
        {visible.map(([label, value], i) => (
          <tr key={i}>
            <td style={{
              padding: "3mm 4mm", color: COLORS.body, width: "55mm",
              verticalAlign: "top", background: COLORS.light,
              fontWeight: 700, borderBottom: `1pt solid ${COLORS.white}`,
            }}>{label}</td>
            <td style={{
              padding: "3mm 4mm", color: COLORS.ink, verticalAlign: "top",
              whiteSpace: "pre-line", background: COLORS.white,
            }}>{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------- Main Component -------------------------------------------------
export default function AbtretungsErklaerung({
  c, gutachterName, user, accessToken, onClauseChange, readOnly = false,
  pageNo, totalPages,
}) {
  const cust = c.customer || {};
  const ins = c.insurance || {};
  const today = new Date().toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  const fullName = [cust.firstname, cust.name].filter(Boolean).join(" ");
  const fullAddress = [cust.address || cust.address_line || cust.street].filter(Boolean).join(", ");
  const plzOrt = [cust.postal_code, cust.city].filter(Boolean).join(" ");
  const accidentDate = fmtDate(c.accident_date || c.order_date || c.insurance?.claim_date);

  // Sachverständigen-/Büro-Daten — fallen auf den eingeloggten User zurück
  const owner = c.owner || user || {};
  const buero = owner.company || user?.company || gutachterName || "";
  const bueroAddrLine = owner.address_line || user?.address_line || "";
  const bueroPLZOrt = [owner.postal_code || user?.postal_code, owner.city || user?.city].filter(Boolean).join(" ");
  const bueroAddr = [bueroAddrLine, bueroPLZOrt].filter(Boolean).join(", ");
  const bueroPhone = owner.phone || user?.phone || owner.mobile || user?.mobile || "";
  const bueroEmail = owner.email || user?.email || "";

  // Letterhead-Logo URL (analog DocumentationStep)
  const letterheadPath = owner.letterhead_logo_path || user?.letterhead_logo_path;
  const letterheadUrl = letterheadPath
    ? `${process.env.REACT_APP_BACKEND_URL || ""}/api/files/download/${encodeURI(letterheadPath)}${accessToken ? `?auth=${encodeURIComponent(accessToken)}` : ""}`
    : null;

  // ── Unterschrift des Geschädigten ─────────────────────────────────────
  // Wird im "Unterschrift"-Step gespeichert als c.signature.storage_path
  // (PNG-Bild des Signature-Pads). Wir laden sie hier via authentifiziertem
  // Download und betten sie an Stelle der typischen Linie ein.
  // signed_at wird zusätzlich als Datum unter "Ort, Datum" verwendet, damit
  // das Datum der echten Unterschrift sichtbar ist (statt dem Render-Datum).
  const sigPath = c.signature?.storage_path;
  const signatureUrl = sigPath
    ? `${process.env.REACT_APP_BACKEND_URL || ""}/api/files/download/${encodeURI(sigPath)}${accessToken ? `?auth=${encodeURIComponent(accessToken)}` : ""}`
    : null;
  const signedDate = (() => {
    const raw = c.signature?.signed_at;
    if (!raw) return today;
    try {
      return new Date(raw).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch { return today; }
  })();

  const doc = {
    caseRef: c.case_reference || c.case_number || c.id?.slice(0, 8),
    schadenNr: ins.claim_number || "",
    gutachterName: owner.name || user?.name || gutachterName || "",
    gutachterCompany: buero,
    gutachterAddress: bueroAddr,
    iban: owner.iban || user?.iban || "",
    bic: owner.bic || user?.bic || "",
    vatId: owner.vat_id || owner.tax_number || user?.vat_id || user?.tax_number || "",
    phone: bueroPhone,
    email: bueroEmail,
    letterheadUrl,
    pageNo,
    totalPages,
  };

  const clauses = useMemo(() => getClauses(c), [c]);

  // ── Responsive Scaling ──────────────────────────────────────────────
  // Misst die tatsächlich verfügbare Breite via ResizeObserver und
  // skaliert die A4-Seiten linear runter, sobald der Container schmaler
  // wird als 210mm (= 794px @ 96dpi). Auf dem Desktop bleibt der
  // Original-Look (scale=1). Vorteil gegenüber CSS `zoom`: funktioniert
  // zuverlässig in allen Browsern, kennt die echte Container-Breite
  // (nicht nur 100vw → würde Sidebar/Padding ignorieren).
  const A4_WIDTH_PX = 794; // 210mm @ 96dpi
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const compute = () => {
      const w = el.offsetWidth || 0;
      const next = w > 0 && w < A4_WIDTH_PX ? w / A4_WIDTH_PX : 1;
      setScale(next);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const updateClause = (id, patch) => {
    if (readOnly || !onClauseChange) return;
    const next = clauses.map((cl) => (cl.id === id ? { ...cl, ...patch } : cl));
    onClauseChange(next);
  };

  const replacements = {
    accident_date: { value: accidentDate },
    gutachter_buero: { value: buero },
  };

  // Pagination — wie viele A4-Pages brauchen wir, und wie verteilen sich
  // die Klauseln darauf?
  const pageCount = calcAbtretungPageCount({ abtretung: { clauses } });
  const clausesPerPage = [];
  let pos = 0;
  for (let i = 0; i < pageCount; i++) {
    const limit = i === 0 ? FIRST_PAGE_CLAUSE_LIMIT : PAGE_CLAUSE_LIMIT;
    clausesPerPage.push(clauses.slice(pos, pos + limit));
    pos += limit;
  }
  const startPage = pageNo || 1;
  const effectiveTotal = totalPages || pageCount;

  // ---------- Helper: rendert eine einzelne A4-Page-Card -------------------
  const renderPage = (pageIdx) => {
    const isFirst = pageIdx === 0;
    const isLast = pageIdx === pageCount - 1;
    const pageClauses = clausesPerPage[pageIdx];
    const continuationOffset = clausesPerPage
      .slice(0, pageIdx)
      .reduce((sum, p) => sum + p.length, 0);
    const pageDoc = { ...doc, pageNo: startPage + pageIdx, totalPages: effectiveTotal };

    return (
      <div
        key={`abtretung-page-${pageIdx}`}
        className="doc-a4 mx-auto bg-white shadow-md relative"
        style={{
          width: "210mm",
          minHeight: "297mm",
          padding: "22mm 20mm 24mm 20mm",
          fontFamily: FONT_STACK,
          fontSize: "10pt",
          lineHeight: 1.5,
          color: COLORS.body,
          marginBottom: "10mm",
          pageBreakAfter: "always",
        }}
      >
        <PageHeader doc={pageDoc} />

        <div style={{ paddingTop: "10mm" }}>
          {isFirst ? (
            <>
              {/* Title-Block — Eyebrow + großer H1 */}
              <div style={{ marginTop: "8mm", marginBottom: "10mm" }}>
                <SectionEyebrow>Sicherungsabtretung · Sachverständigenhonorar</SectionEyebrow>
                <h1 style={{
                  fontSize: "28pt", fontWeight: 700, color: COLORS.ink,
                  margin: 0, lineHeight: 1.15, letterSpacing: "-0.01em",
                }}>Abtretungserklärung</h1>
                <div style={{ marginTop: "3mm", fontSize: "9pt", color: COLORS.mute }}>
                  {[cust.city, today].filter(Boolean).join(", ")}
                </div>
              </div>

              {/* Auftraggeber / Geschädigter */}
              <div style={{ marginTop: "6mm" }}>
                <SectionEyebrow>Auftraggeber · Geschädigter</SectionEyebrow>
                <KVList rows={[
                  ["Name", fullName],
                  ["Anschrift", fullAddress],
                  ["PLZ / Ort", plzOrt],
                ]} />
              </div>

              {/* Unfallgegner / Versicherung */}
              <div style={{ marginTop: "6mm" }}>
                <SectionEyebrow>Unfallgegner · Versicherung</SectionEyebrow>
                <KVList rows={[
                  ["Versicherung", ins.opponent_insurer || ins.insurer_name || ins.insurer],
                  ["Schadennummer", ins.claim_number],
                  ["Unfallzeitpunkt", accidentDate],
                ]} />
              </div>
            </>
          ) : (
            <div style={{
              fontSize: "8pt", fontWeight: 700, color: COLORS.mute,
              letterSpacing: "0.1em", textTransform: "uppercase",
              marginTop: "2mm", marginBottom: "6mm",
            }}>Abtretungserklärung · Fortsetzung</div>
          )}

          {/* Klauseln dieser Seite */}
          <div style={{ marginTop: isFirst ? "10mm" : 0 }}>
            {pageClauses.map((cl, idx) => (
              <EditableClause
                key={cl.id}
                num={continuationOffset + idx + 1}
                title={cl.title}
                text={cl.text}
                replacements={replacements}
                readOnly={readOnly}
                onChange={(patch) => updateClause(cl.id, patch)}
              />
            ))}
          </div>

          {/* Ort/Datum + Unterschrift nur auf der letzten Seite */}
          {isLast && (
            <div style={{
              marginTop: "16mm",
              display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12mm",
            }}>
              <div>
                <SectionEyebrow>Ort, Datum</SectionEyebrow>
                <div style={{
                  borderBottom: `1pt solid ${COLORS.ink}`,
                  paddingBottom: "8mm", color: COLORS.ink, fontWeight: 500,
                }}>
                  {[cust.city, signedDate].filter(Boolean).join(", ") || "—"}
                </div>
              </div>
              <div>
                <SectionEyebrow>Unterschrift Geschädigter</SectionEyebrow>
                <div style={{
                  borderBottom: `1pt solid ${COLORS.ink}`,
                  paddingBottom: signatureUrl ? "1mm" : "8mm",
                  color: COLORS.ink, fontWeight: 500,
                  position: "relative",
                  minHeight: "12mm",
                  display: "flex", alignItems: "flex-end",
                }}>
                  {signatureUrl ? (
                    <img
                      src={signatureUrl}
                      alt="Unterschrift Geschädigter"
                      crossOrigin="anonymous"
                      style={{
                        maxHeight: "16mm",
                        maxWidth: "100%",
                        objectFit: "contain",
                        objectPosition: "left bottom",
                      }}
                      data-testid="abtretung-signature-img"
                    />
                  ) : (
                    <span style={{ color: COLORS.mute, fontStyle: "italic" }}>{fullName || "—"}</span>
                  )}
                </div>
                <div style={{ fontSize: "7.5pt", color: COLORS.mute, marginTop: "1.5mm" }}>
                  {fullName ? fullName : "Unterschriftslinie für den Geschädigten"}
                </div>
              </div>
            </div>
          )}
        </div>

        <PageFooter doc={pageDoc} />
      </div>
    );
  };

  // Gesamthöhe (mm) aller Seiten inkl. Spacing zwischen den Seiten.
  // Jede Page hat minHeight 297mm + marginBottom 10mm.
  const totalHeightMm = pageCount * (297 + 10);

  return (
    <div
      ref={containerRef}
      className="w-full"
      data-testid="abtretungserklaerung-paper"
      style={{
        // Reserviert nur den tatsächlich sichtbaren (skalierten) Platz —
        // ohne dies würde unter den skalierten Pages eine riesige
        // Leerfläche entstehen, weil transform:scale die Layout-Box
        // nicht verändert.
        height: scale < 1 ? `${totalHeightMm * scale}mm` : "auto",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: "210mm",
          // Mittig wie die Gutachten-Seiten (mx-auto); bei scale<1 (Mobile)
          // füllt die skalierte Seite exakt die Containerbreite von links.
          margin: "0 auto",
          transformOrigin: "top left",
          transform: scale < 1 ? `scale(${scale})` : "none",
        }}
      >
        {Array.from({ length: pageCount }, (_, i) => renderPage(i))}
      </div>
    </div>
  );
}

// ---------- EditableClause — Click-to-Edit Titel + Text -------------------
function EditableClause({ num, title, text, replacements, readOnly, onChange }) {
  const [editingTitle, setEditingTitle] = React.useState(false);
  const [editingText, setEditingText] = React.useState(false);
  const [draftTitle, setDraftTitle] = React.useState(title);
  const [draftText, setDraftText] = React.useState(text);

  React.useEffect(() => { setDraftTitle(title); }, [title]);
  React.useEffect(() => { setDraftText(text); }, [text]);

  const saveTitle = () => {
    setEditingTitle(false);
    const v = draftTitle.trim();
    if (v && v !== title) onChange({ title: v });
  };
  const saveText = () => {
    setEditingText(false);
    const v = draftText.trim();
    if (v && v !== text) onChange({ text: v });
  };

  const editableStyle = readOnly ? {} : {
    cursor: "text", borderRadius: "2px", padding: "0 3px", margin: "0 -3px",
  };

  return (
    <div style={{ marginBottom: "5mm" }} data-testid={`abtretung-clause-${num}`}>
      <div style={{
        fontSize: "10pt", fontWeight: 700, color: COLORS.ink,
        marginBottom: "1.5mm", display: "flex", alignItems: "baseline", gap: "6px",
      }}>
        <span style={{ color: COLORS.accent, minWidth: "12px" }}>{num}.</span>
        {editingTitle && !readOnly ? (
          <input
            autoFocus
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onBlur={saveTitle}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveTitle(); } }}
            style={{
              flex: 1, fontSize: "10pt", fontWeight: 700, color: COLORS.ink,
              background: COLORS.light, border: "none", borderBottom: `1.5pt solid ${COLORS.accent}`,
              outline: "none", padding: "1px 4px", fontFamily: FONT_STACK,
            }}
            data-testid={`abtretung-clause-${num}-title-input`}
          />
        ) : (
          <span
            onClick={() => !readOnly && setEditingTitle(true)}
            style={{ flex: 1, ...editableStyle }}
            title={readOnly ? "" : "Klicken zum Bearbeiten"}
            data-testid={`abtretung-clause-${num}-title`}
          >
            {title}
          </span>
        )}
      </div>
      {editingText && !readOnly ? (
        <textarea
          autoFocus
          value={draftText}
          onChange={(e) => setDraftText(e.target.value)}
          onBlur={saveText}
          rows={Math.max(3, draftText.split("\n").length + 1)}
          style={{
            width: "100%", fontSize: "10pt", lineHeight: 1.55, color: COLORS.body,
            background: COLORS.light, border: `0.5pt solid ${COLORS.border}`,
            borderRadius: "2px", padding: "6px 8px", outline: "none",
            fontFamily: FONT_STACK, resize: "vertical",
          }}
          data-testid={`abtretung-clause-${num}-text-input`}
        />
      ) : (
        <p
          onClick={() => !readOnly && setEditingText(true)}
          style={{
            color: COLORS.body, lineHeight: 1.55, margin: 0,
            paddingLeft: "18px", textAlign: "justify",
            ...editableStyle,
          }}
          title={readOnly ? "" : "Klicken zum Bearbeiten"}
          data-testid={`abtretung-clause-${num}-text`}
        >
          {renderTextWithFields(text, replacements)}
        </p>
      )}
    </div>
  );
}
